import { useQuery } from '@tanstack/react-query'
import { getContentRepository } from './repositoryProvider'
import { contentQueries, contentQueryKey, type ContentQuery } from './queries'
import { useTranslation } from '@/lib/i18n/LocaleContext'

type AsyncState<T> = { data: T | null; loading: boolean; error: Error | null }

/**
 * Reads CMS content through the shared TanStack Query cache. On a server
 * render the route loader has already prefetched the content (see
 * `prefetch.server.ts`), so the first render — server and hydration alike —
 * has the data and no loading state. Anything a loader did not prefetch is
 * fetched in the browser after hydration, as before.
 *
 * The locale is part of the cache key, so switching language fetches the
 * content in the new locale.
 */
function useContent<T>(q: ContentQuery<T>): AsyncState<T> {
  const { locale } = useTranslation()
  const result = useQuery({
    queryKey: contentQueryKey(locale, q),
    queryFn: () => q.load(getContentRepository(locale)),
  })
  return { data: result.data ?? null, loading: result.isPending, error: result.error }
}

export const usePage = (slug: string) => useContent(contentQueries.page(slug))
export const useHeader = () => useContent(contentQueries.header())
export const useFooter = () => useContent(contentQueries.footer())
export const useSiteSettings = () => useContent(contentQueries.siteSettings())
export const useMaterials = (category?: string) => useContent(contentQueries.materials(category))
export const useFeaturedMaterials = () => useContent(contentQueries.featuredMaterials())
export const useMaterial = (slug: string) => useContent(contentQueries.material(slug))
export const useCategories = () => useContent(contentQueries.categories())
export const useUpcomingEvents = () => useContent(contentQueries.upcomingEvents())
export const useLatestNews = () => useContent(contentQueries.latestNews())
